import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

export const EASE = [0.16, 1, 0.3, 1];

const cache = new Map();
/** Resolve a tag name ('div', 'section', 'li'…) or component to a motion component (memoised). */
export function motionTag(as) {
  if (!cache.has(as)) cache.set(as, typeof as === 'string' ? motion[as] || motion.div : motion(as));
  return cache.get(as);
}

// How long a scroll-reveal may stay hidden before the safety net re-checks it against the viewport.
const SAFETY_MS = 900;

function intersectsViewport(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return false;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
}

/**
 * Scroll-reveal trigger that can never leave content hidden. Visible when the IntersectionObserver says so,
 * and also (a) immediately if the element is already on screen at mount (e.g. after a route change or
 * scroll restoration), (b) on scroll/resize once it reaches the viewport, and (c) after a short timeout if it
 * is on screen by then. Returns `[ref, shown]`.
 */
export function useRevealInView({ once = true, amount = 0.2 } = {}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, amount });
  const [forced, setForced] = useState(false);

  useEffect(() => {
    if (inView || forced) return undefined;
    const check = () => {
      if (intersectsViewport(ref.current)) setForced(true);
    };
    check();
    const timer = setTimeout(check, SAFETY_MS);
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [inView, forced]);

  // The fallback only ever reveals: once forced visible, it stays visible.
  return [ref, inView || forced];
}

/** Fades + rises its children into view once. `<Reveal as="section" delay={0.1} y={20}>` */
export default function Reveal({ as = 'div', delay = 0, y = 20, duration = 0.6, once = true, amount = 0.2, className = '', children, ...rest }) {
  const Tag = motionTag(as);
  const reduce = useReducedMotion();
  const [ref, shown] = useRevealInView({ once, amount });
  return (
    <Tag
      ref={ref}
      className={className}
      // Reduced motion: render in place, no hidden starting state at all.
      initial={reduce ? false : { opacity: 0, y }}
      animate={reduce || shown ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
